import { TableColumn } from './table-column';
import { LocalStorageService } from '../services';
import { TranslateService } from '@ngx-translate/core';
import { ChangeDetectorRef, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export class TableColumnManager {
    private selectedColumns!: TableColumn[];
    private _translateService: TranslateService;
    private readonly _cdr = inject(ChangeDetectorRef, { optional: true });
    /** Original translation key of each column label, by column id, so labels can be re-translated on language change */
    private readonly _labelKeys = new Map<string, string>();

    constructor(
        private readonly stateKey: string,
        public columns: TableColumn[],
        private readonly localStorage: LocalStorageService
    ) {
        this._translateService = inject(TranslateService);
        this.columns.forEach(col => {
            if (col.label) {
                this._labelKeys.set(col.id, col.label);
            }
        });
        this.translateLabels();
        this.selectedColumns = this.resolveSelectedColumns();

        this._translateService.onLangChange
            .pipe(takeUntilDestroyed(inject(DestroyRef)))
            .subscribe(() => {
                this.translateLabels();
                this._cdr?.markForCheck();
            });
    }

    private translateLabels(): void {
        this.columns.forEach(col => {
            const labelKey = this._labelKeys.get(col.id);
            if (labelKey) {
                col.label = this._translateService.instant(labelKey);
                col.header = col.label;
            }
        });
    }

    private static encodeForLocalStorage(columns: TableColumn[]): string[] {
        return columns
            .filter(element => element !== undefined)
            .map(e => e.id);
    }

    public getSelectedColumns(): TableColumn[] {
        return this.selectedColumns;
    }

    public setSelectedColumns(columns: TableColumn[]): void {
        this.selectedColumns = columns;
        this.storeColumns(columns);
    }

    public updateColumns(columns: TableColumn[]): void {
        this.columns = columns;
        this.selectedColumns = this.resolveSelectedColumns();
    }

    private decodeFromLocalStorage(columnIds: string[]): TableColumn[] {
        const columns = [...this.columns.filter(e => columnIds.includes(e.id))];
        return columns.sort(
            (a, b) => columnIds.indexOf(a.id) - columnIds.indexOf(b.id)
        );
    }

    private resolveSelectedColumns(): TableColumn[] {
        const storedColumns = this.localStorage.getString(this.stateKey);
        if (storedColumns) {
            return this.decodeFromLocalStorage(JSON.parse(storedColumns));
        }

        const initialColumns = this.columns.filter(e => e.default);

        this.storeColumns(initialColumns);

        return initialColumns;
    }

    private storeColumns(columns: TableColumn[]): void {
        this.localStorage.setObject(
            this.stateKey,
            TableColumnManager.encodeForLocalStorage(columns)
        );
    }
}
